import React, { useState, useEffect } from 'react'

import {
  Card,
  Grid,
  Input,
  Picker,
  SelectForm,
  Form,
  Button,
  Span,
  Row,
  RadioGroup,
  Col
} from 'components'
import { MagnifyingGlassIcon } from 'components/Icons'
import { useModal, useToggle, usePersistForm } from 'hooks'
import useLoading from 'hooks/useLoading'
import moment from 'moment'
import { ViewContractModal } from 'pages/NGT/CRE/components/ViewContractModal/ViewContractModal'
import { useForm } from 'react-hook-form'
import {
  RecordsSheetServices,
  MotivosBloqueioInterface,
  MotivoBloqueioServices
} from 'services/NGT/RecordsSheet'
import { COLORS, SPACING } from 'utils'
import { yup, yupResolver } from 'utils/yup'

import { IRedirectData } from '../..'
import { TableSearchRecordsSheet } from './components'

export interface SearchRecordsSheet {
  setSelectedTab: any
  setDataTable: any
  redirectData: IRedirectData
  motivosBloqueio: MotivosBloqueioInterface[]
}

export interface IValueFilters {
  CdContrato: string
  DataBloqueioInicio: string
  DataBloqueioFim: string
  DataLiberacaoInicio: string
  DataLiberacaoFim: string
  MotivoId: number
  NmFornecedor: string
  TxReferencia: string
  TxDocumento: string
  CdFolhaRegistroSRV: string
  FlBloqueado: boolean
}

const schema = yup.object().shape({
  DataBloqueioFim: yup
    .string()
    .date()
    .daterange({
      compare: 'DataBloqueioInicio',
      message: 'Data não pode ser menor que Data Bloqueio Início',
      period: 'end'
    })
    .nullable(),
  DataBloqueioInicio: yup
    .string()
    .date()
    .daterange({
      compare: 'DataBloqueioFim',
      message: 'Data não pode ser maior que Data Bloqueio Fim',
      period: 'begin'
    })
    .nullable(),
  DataLiberacaoFim: yup
    .string()
    .date()
    .daterange({
      compare: 'DataLiberacaoInicio',
      message: 'Data não pode ser menor que Data Liberação Início',
      period: 'end'
    })
    .nullable(),
  DataLiberacaoInicio: yup
    .string()
    .date()
    .daterange({
      compare: 'DataLiberacaoFim',
      message: 'Data não pode ser maior que Data Liberação Fim',
      period: 'begin'
    })
    .nullable()
})

const SearchRecordsSheet: React.FC<SearchRecordsSheet> = ({
  setSelectedTab,
  setDataTable,
  redirectData,
  motivosBloqueio
}) => {
  const [tableData, setTableData] = useState([])
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [contractID, setContractID] = useState<number>(0)
  const [showContractModal, toggleContractModal] = useToggle()
  const [pageSize, setPageSize] = useState(15)

  const [valueFilters, setValueFilters] = useState<IValueFilters>({
    CdContrato: null,
    DataBloqueioFim: null,
    DataBloqueioInicio: null,
    DataLiberacaoFim: null,
    DataLiberacaoInicio: null,
    MotivoId: null,
    NmFornecedor: null,
    TxReferencia: null,
    TxDocumento: null,
    CdFolhaRegistroSRV: null,
    FlBloqueado: null
  })

  const { showModal } = useModal()

  const { setLoading } = useLoading()
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    getValues,
    watch
  } = useForm({
    resolver: yupResolver(schema)
  })
  usePersistForm('folha-registro-form', {
    setValue,
    watch
  })

  const getTableData = () => {
    setLoading('loading')
    let obj = {}
    if (getValues()) {
      obj = getValues()
    }
    obj['PageNumber'] = page
    obj['PageSize'] = pageSize

    obj['DataBloqueioFim'] = moment(obj['DataBloqueioFim']).toISOString()
    obj['DataBloqueioInicio'] = moment(obj['DataBloqueioInicio']).toISOString()
    obj['DataLiberacaoFim'] = moment(obj['DataLiberacaoFim']).toISOString()
    obj['DataLiberacaoInicio'] = moment(
      obj['DataLiberacaoInicio']
    ).toISOString()

    RecordsSheetServices()
      .GetByFilters(obj)
      .then(({ data }) => {
        setTableData(data.items)
        setTotalItems(data.pageCount)
        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            'Ocorreu um erro e não foi possível encontrar os dados pesquisados',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const handleSubmitForm = (data: IValueFilters) => {
    const value = data

    if (value.DataBloqueioInicio) {
      value.DataBloqueioInicio = moment(value.DataBloqueioInicio).toISOString()
    }

    if (value.DataLiberacaoInicio) {
      value.DataLiberacaoInicio = moment(
        value.DataLiberacaoInicio
      ).toISOString()
    }

    if (value.DataBloqueioFim) {
      value.DataBloqueioFim = moment(value.DataBloqueioFim).toISOString()
    }

    if (value.DataLiberacaoFim) {
      value.DataLiberacaoFim = moment(value.DataLiberacaoFim).toISOString()
    }

    Object.keys(value).forEach((key) => {
      if (
        value[key] === undefined ||
        value[key] === null ||
        value[key] === ''
      ) {
        delete value[key]
      }
    })
    page === 1 ? setValueFilters(value) : setPage(1)
  }

  const resetValues = () => {
    reset({
      CdContrato: '',
      DataBloqueioFim: null,
      DataBloqueioInicio: null,
      DataLiberacaoFim: null,
      DataLiberacaoInicio: null,
      MotivoId: null,
      NmFornecedor: ''
    })

    setValueFilters({
      CdContrato: null,
      DataBloqueioFim: null,
      DataBloqueioInicio: null,
      DataLiberacaoFim: null,
      DataLiberacaoInicio: null,
      MotivoId: null,
      NmFornecedor: null,
      TxReferencia: null,
      TxDocumento: null,
      CdFolhaRegistroSRV: null,
      FlBloqueado: null
    })

    setPage(1)
  }

  const handleVerContrato = (idContrato: number) => {
    setContractID(idContrato)
    toggleContractModal()
  }

  const getFilterRedirect = async () => {
    setTimeout(() => {
      setTableData([])
      setValue('NmFornecedor', redirectData?.nmFornecedor)
      setValueFilters((oldState) => ({
        ...oldState,
        NmFornecedor: redirectData?.nmFornecedor
      }))
    }, 600)
  }

  useEffect(() => {
    setTableData([])
    getTableData()
  }, [pageSize, page, valueFilters])

  useEffect(() => {
    if (redirectData?.nmFornecedor?.length > 0) {
      getFilterRedirect()
    }
  }, [])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        backgroundColor={COLORS.white}
        borderRadius={`0px 10px 0px 0px`}
      >
        <Form onSubmit={handleSubmit(handleSubmitForm)}>
          <Grid
            gridCols="1fr 2fr 1fr 1fr 0.1fr 1fr"
            gridGap={`${SPACING.spacing20}`}
            margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          >
            <Input
              label="Contrato"
              placeholder="Contrato"
              control={control}
              formValue="CdContrato"
              errors={errors}
            />
            <Input
              label="Fornecedor"
              placeholder="Fornecedor"
              control={control}
              formValue="NmFornecedor"
              errors={errors}
            />
            <SelectForm
              defaultValue={null}
              listOptions={motivosBloqueio}
              label="Motivo"
              clearSelect
              placeholder="Selecione"
              labelMenu="txDescricao"
              valueMenu={'idMotivoBloqueio'}
              control={control}
              errors={errors}
              formValue="MotivoId"
            />
            <Picker
              formValue="DataBloqueioInicio"
              defaultValue={null}
              label="Data Bloqueio (Início)"
              control={control}
              errors={errors}
            />
            <Span
              text="à"
              marginLeft={SPACING.spacing15}
              marginRight={SPACING.spacing15}
              marginTop={SPACING.spacing30}
            />
            <Picker
              formValue="DataBloqueioFim"
              defaultValue={null}
              label="Data Bloqueio (Fim)"
              control={control}
              errors={errors}
            />
          </Grid>
          <Row justifyContent="flex">
            <Grid
              gridCols="0.1fr 0.001fr 0.1fr 1fr 1fr 1fr 1fr"
              gridGap={`${SPACING.spacing20}`}
              margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
            >
              <Picker
                formValue="DataLiberacaoInicio"
                defaultValue={null}
                label="Data Liberação (Início)"
                control={control}
                errors={errors}
              />
              <Span
                text="à"
                marginLeft={SPACING.spacing15}
                marginRight={SPACING.spacing15}
                marginTop={SPACING.spacing30}
              />
              <Picker
                formValue="DataLiberacaoFim"
                defaultValue={null}
                label="Data Liberação (Fim)"
                control={control}
                errors={errors}
              />
              <Input
                label="Referencia"
                placeholder="N°"
                control={control}
                formValue="TxReferencia"
                maxLength={50}
                errors={errors}
              />
              <Input
                label="Documento"
                placeholder="N°"
                control={control}
                formValue="TxDocumento"
                errors={errors}
                maxLength={50}
              />
              <Input
                label="Folha de Registro"
                placeholder="N°"
                control={control}
                formValue="CdFolhaRegistroSRV"
                errors={errors}
                maxLength={50}
              />


              <Col marginBottom={SPACING.spacing5}>
                <RadioGroup
                  label="Bloqueado"
                  formValue="FlBloqueado"
                  listOptions={[
                    { label: 'Todos', value: '1' },
                    { label: 'Sim', value: '2' },
                    { label: 'Não', value: '3' },
                    // { label: 'Total', value: 'null' }
                  ]}
                  defaultValue={'1'}
                  row
                  control={control}
                  errors={errors}
                />
              </Col>
            </Grid>
          </Row>
          <Row justifyContent="flex-end" alignItems="center">
            <Button
              text="Limpar"
              variantButton="text"
              onClick={() => resetValues()}
            />
            <div style={{ width: '1rem' }} />
            <Button
              endIcon={<MagnifyingGlassIcon />}
              type="submit"
              text="Pesquisar"
            />
          </Row>
        </Form>
        <TableSearchRecordsSheet
          setDataTable={setDataTable}
          tableData={tableData}
          setPage={setPage}
          totalItems={totalItems}
          pageSize={pageSize}
          setPageSize={setPageSize}
          page={page}
          handleVerContrato={handleVerContrato}
        />
      </Card>
      <ViewContractModal
        contractID={contractID}
        showModal={showContractModal}
        toggleModal={toggleContractModal}
      />
    </>
  )
}

export default SearchRecordsSheet
